export type Vec = number[]
export type Vec3 = [number, number, number]
export type Vec2 = [number, number]

export let lift_binop = (f: (x: number, y: number) => number): (<T extends number[]>(u: T, v: T) => T) =>
    <T extends Vec>(u: T, v: T) => u.map((x, i) => f(x, v[i])) as T
export let lift_unop = (f: (x: number) => number): (<T extends Vec>(u: T) => T) =>
    <T extends Vec>(u: T) => u.map(x => f(x)) as T
let fold0 = (f: (x: number, y: number) => number): (<T extends Vec>(u: T) => number) =>
    <T extends Vec>(u: T) => u.reduce(f)

export let [add, sub] = [(x: number, y: number) => x + y, (x: number, y: number) => x - y].map(lift_binop)
export let [mul, div] = [(x: number, y: number) => x * y, (x: number, y: number) => x / y]
    .map(f => <T extends Vec>(v: T, scalar: number) => lift_unop(c => f(c, scalar))(v));
export let [sum, square] = [fold0((x, y) => x + y), lift_unop(x => x ** 2)]
export let norm = <T extends Vec>(v: T) => Math.sqrt(sum(square(v)))
export let dot = <T extends Vec>(u: T, v: T) => sum(lift_binop((x, y) => x * y)(u, v))
export let normalize = <T extends Vec>(v: T) => div(v, norm(v))
export let normTo1 = <T extends Vec>(v: T) => div(v, sum(v));
export let round = <T extends Vec>(v: T, p = 10 ** 5, f = (v: number) => Math.round(v * p) / p): T => v.map(f) as T
export let eq = <T extends Vec>(u: T, v: T): boolean => u.every<number>((x: number, i: number): x is number => x === v[i])
export let random3 = (): Vec3 => normTo1([Math.random(), Math.random(), Math.random()])

export let [e, f1, f2]: Vec3[] = [div([1, 1, 1], 3), normalize([0, -1, 1]), normalize([-2, 1, 1])];
export let [X, Y, Z]: [Vec3, Vec3, Vec3] = [[1, 0, 0], [0, 1, 0], [0, 0, 1]];

export let proj = (x: Vec3): Vec2 => [dot(x, f1), dot(x, f2)]
export let unproj = (x: Vec2): Vec3 => add(e, add(mul(f1, x[0]), mul(f2, x[1])))

export let test = (silent = true) => [X, Y, Z, ...Array(10).fill(0).map(random3)].forEach(v => {
    let p = proj(v);
    let [u, rv] = [round(unproj(p)), round(v)];
    if (!eq(rv, u))
        throw new Error('Failed tests');
    silent || console.log(rv, '-proj->', round(p), '-unproj->', u);
});

test();

